import { formatDate } from "../../Helper/generalhelper";
import { valuesAreEquivalent } from "./scoopChangedFields";

const PROJECT_LOG_STATUS = "project";

// Labels from General/Form.js. Unknown keys, including project_createdBy, stay hidden.
const PROJECT_FIELD_LABELS = {
  client: "Client",
  contact: "Contact Person",
  indirect_customer: "Account",
  specialization: "Specialization",
  project_manager: "Project Manager",
  sub_pm: "Substitute Project Manager",
  project_coordinator: "Project Coordinator",
  sub_pc: "Substitute Project Coordinator",
  QA_specialist: "Quality Assurance Analyst",
  sub_qa: "Substitute QA",
  order_date: "Project Date",
};

const ID_KEYS = new Set([
  "client",
  "contact",
  "indirect_customer",
  "specialization",
  "project_manager",
  "sub_pm",
  "project_coordinator",
  "sub_pc",
  "QA_specialist",
  "sub_qa",
]);

const USER_KEYS = new Set([
  "project_manager",
  "sub_pm",
  "project_coordinator",
  "sub_pc",
  "QA_specialist",
  "sub_qa",
]);

const PREVIEW_LIMIT = 4;

export function isProjectLog(logStatus) {
  return String(logStatus || "").trim().toLowerCase() === PROJECT_LOG_STATUS;
}

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return null;
  }
}

function parseChangedFields(details) {
  const parsed = parseDetails(details);
  if (!parsed) return null;

  let changedFields = parsed.changedFields;
  if (typeof changedFields === "string") {
    try {
      changedFields = JSON.parse(changedFields);
    } catch (e) {
      return null;
    }
  }

  if (!changedFields || typeof changedFields !== "object" || Array.isArray(changedFields)) {
    return null;
  }

  return changedFields;
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed.toLowerCase() === "null" || trimmed === "[]" || trimmed === "{}";
  }
  return false;
}

function isEmptyId(value) {
  if (isEmptyValue(value)) return true;
  if (value === 0 || value === "0") return true;
  return false;
}

function isZeroDate(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim().startsWith("0000-00-00");
}

function isDiffShape(change) {
  if (!change || typeof change !== "object" || Array.isArray(change)) return false;
  return (
    Object.prototype.hasOwnProperty.call(change, "old") ||
    Object.prototype.hasOwnProperty.call(change, "new")
  );
}

function lookupName(value, nameMap) {
  if (isEmptyId(value)) return "";
  const id = String(value).trim();
  return nameMap?.[id] || id;
}

function formatTime(value) {
  if (isEmptyValue(value)) return "";
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function formatDateOnly(value) {
  if (isEmptyValue(value) || isZeroDate(value)) return "";
  return formatDate(String(value).trim(), { includeTime: false });
}

function splitDue(value) {
  if (isEmptyValue(value) || isZeroDate(value)) return { date: "", time: "" };
  const raw = String(value).trim();
  const parts = raw.split(/[T ]/);
  return {
    date: formatDateOnly(parts[0]),
    time: formatTime(parts[1] || ""),
  };
}

function formatProjectDate(value) {
  if (isEmptyValue(value) || isZeroDate(value)) return "";
  const raw = String(value).trim();
  const time = formatTime(raw.split(/[T ]/)[1] || "");
  const date = formatDateOnly(raw);
  if (!date) return "";
  return time ? `${date} | ${time}` : date;
}

function formatIdField(fieldKey, value, lookups) {
  if (fieldKey === "client") return lookupName(value, lookups.clientNames);
  if (fieldKey === "contact") return lookupName(value, lookups.contactNames);
  if (fieldKey === "indirect_customer") return lookupName(value, lookups.accountNames);
  if (fieldKey === "specialization") return lookupName(value, lookups.specializationNames);
  if (USER_KEYS.has(fieldKey)) return lookupName(value, lookups.userNames);
  return isEmptyValue(value) ? "" : String(value);
}

export function collectProjectContactIds(detailsList) {
  const ids = new Set();
  (Array.isArray(detailsList) ? detailsList : []).forEach(details => {
    const changedFields = parseChangedFields(details);
    const contact = changedFields?.contact;
    if (!contact) return;
    [contact.old, contact.new].forEach(value => {
      if (isEmptyId(value)) return;
      ids.add(String(value).trim());
    });
  });
  return [...ids];
}

export function getProjectChangedFields(details, lookups = {}) {
  const changedFields = parseChangedFields(details);
  if (!changedFields) return [];

  const rows = [];

  Object.keys(changedFields).forEach(key => {
    const change = changedFields[key];
    if (!isDiffShape(change)) return;

    if (key === "due_date") {
      const oldParts = splitDue(change.old);
      const newParts = splitDue(change.new);
      if (oldParts.date !== newParts.date) {
        rows.push({ key: "due_date", label: "Due Date", old: oldParts.date, new: newParts.date });
      }
      if (oldParts.time !== newParts.time) {
        rows.push({ key: "due_time", label: "Due Time", old: oldParts.time, new: newParts.time });
      }
      return;
    }

    if (!PROJECT_FIELD_LABELS[key]) return;

    if (key === "order_date") {
      const oldDate = formatProjectDate(change.old);
      const newDate = formatProjectDate(change.new);
      if (oldDate === newDate) return;
      rows.push({ key, label: PROJECT_FIELD_LABELS[key], old: oldDate, new: newDate });
      return;
    }

    if (ID_KEYS.has(key) && isEmptyId(change.old) && isEmptyId(change.new)) return;
    if (valuesAreEquivalent(change.old, change.new, key)) return;

    const oldValue = formatIdField(key, change.old, lookups);
    const newValue = formatIdField(key, change.new, lookups);
    if (oldValue === newValue) return;

    rows.push({
      key,
      label: PROJECT_FIELD_LABELS[key],
      old: oldValue,
      new: newValue,
    });
  });

  return rows;
}

export function getProjectChangedSummary(changes) {
  const displayable = Array.isArray(changes) ? changes : [];
  const previewLabels = displayable.slice(0, PREVIEW_LIMIT).map(change => change.label);
  const remaining = Math.max(displayable.length - PREVIEW_LIMIT, 0);

  return {
    text: previewLabels.length ? `Changed: ${previewLabels.join(", ")}` : "",
    remaining,
  };
}
